import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Carousel, Dropdown } from 'react-bootstrap';
import { FaExclamationCircle } from 'react-icons/fa';

import { UseOutletContextCategory } from './Category';
import { StateLanguage } from '../../../../languages/config/StateLanguage';
import { CategoryDA } from '../../../../services/admin/category.service';
import { Category } from '../../../../models/category.model';
import { Error } from '../../../../models/error.model';
import { Message } from '../../../../models/message.model';
import { GlobalSwal } from '../../../../styles/swal.style';
import { modalShow } from '../../../../utils/modal.util';
import { imageProduct, imageService } from '../../../../scripts/image.value.script';
import { accountingAccountSelected } from '../../../../scripts/list.selected.script';
import { languageValue } from '../../../../libraries/language.library';
import { uploadTooltip } from '../../../../utils/tooltip.util';
import { getError, getMessage } from '../../../../languages/translations/response';
import { errorWebHandler } from '../../../../scripts/error.web.handler.script';

import AppModalCategoryUpdate from '../../../../components/modal/ModalCategoryUpdate';
import AppModalCategoryUpdateListing from '../../../../components/modal/ModalCategoryUpdateListing';
import AppPreloader from '../../../../components/Preloader';
import imageSearch from '../../../../assets/themes/images/search.svg';
import EmptyState from '../../../../components/element/EmptyState';
import imgServiceDefault from '../../../../assets/themes/service.png';
import imgProductDefault from '../../../../assets/themes/product.png';

export interface CategoryViewPageProps {};

let errorResponse: Error, messageResponse: Message, categoryResponse: Category;

const CategoryViewPage: React.FunctionComponent<CategoryViewPageProps> = props => {
  const {setRoute, companyForUser, servicesForCompany, productsForCompany, categoriesForCompany, accountingExpenseAccountsForCompany, accountingVatAccountsForCompany, accountingIncomeAccountsForCompany, setCategoriesForCompany, loadServicesForCompany, loadProductsForCompany} = UseOutletContextCategory()
  const {lang} = StateLanguage()
  const param = useParams()
  const navigate = useNavigate()

  const [mounted, setMounted] = useState(false)
  const [category, setCategory] = useState<Category | null>(null)
  const [categoryServices, setCategoryServices] = useState<string[] | null>([])
  const [categoryProducts, setCategoryProducts] = useState<string[] | null>([])

  const loadCategory = async (id_company: string, id_category: string) => {
    await CategoryDA.getCategory(id_company, id_category).then( (response) => {
      if (response.status === 200) {
        categoryResponse = response.data?.data ?? response.data
        setCategory(categoryResponse)
      } else {
        errorResponse = response.data

        GlobalSwal.fire({
          title: getError(errorResponse.code, lang.code, errorResponse.message),
          text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
          icon: 'error',
          confirmButtonText: lang.labels.okGotIt
        })
      }
    }).catch( (error) => {
      errorWebHandler(lang, error)
    })
  }

  const submitCategoryDelete = async () => {
    if (companyForUser && categoriesForCompany && category) {
      GlobalSwal.fire({
        title: lang.labels.youWantToDeleteThisCategory,
        text: lang.labels.youWillNotBeAbleToUndo,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: lang.labels.yesDelete,
        cancelButtonText: lang.labels.noCancel
      }).then(async (result) => {
        if (result.isConfirmed) {
          await CategoryDA.deleteCategories(companyForUser.id, [category.id]).then( (response) => {
            if (response.status === 200) {
              messageResponse = response.data
              setCategoriesForCompany(categoriesForCompany.filter((temp) => (temp.id !== category.id)))

              GlobalSwal.fire({
                title: getMessage(messageResponse.message, lang.code),
                text: lang.labels.actionCompletedReturningToPage,
                icon: 'success',
                showConfirmButton: false,
                timer: 2200
              }).then( () => {
                navigate('/main/inventory/category/list', {replace: true})
              })
            } else {
              errorResponse = response.data

              GlobalSwal.fire({
                title: getError(errorResponse.code, lang.code, errorResponse.message),
                text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
                icon: 'error',
                confirmButtonText: lang.labels.okGotIt
              })
            }
          }).catch( (error) => {
            errorWebHandler(lang, error)
          })
        }
      })
    }
  }

  const executeShowModalCategoryUpdate = () => {
    modalShow('modal-category-update')
  }

  const executeShowModalCategoryUpdateListing = () => {
    if (category) {
      setCategoryServices(category.services.map((item) => (item.id)))
      setCategoryProducts(category.products.map((item) => (item.id)))
      modalShow('modal-category-update-listing')
    }
  }

  useEffect( () => {
    if (companyForUser && param.id) {
      loadCategory(companyForUser.id, param.id)
      uploadTooltip()
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyForUser, param])

  useEffect( () => {
    setMounted(true)
    setRoute({path: {root: lang.labels.categories, branch: lang.labels.viewCategory}, company: false})

    return () => setMounted(false)

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (!mounted) return null

  return (
    <div className="w-100 h-100">
      { companyForUser && accountingExpenseAccountsForCompany && accountingVatAccountsForCompany && accountingIncomeAccountsForCompany && category
        ?
        <div className="d-flex flex-column flex-lg-row">
          <div className="flex-lg-row-auto w-100 w-lg-400px mb-5 mb-lg-0">
            <div className="card card-flush">
              <div className="card-header pt-3">
                <div className="card-title align-items-start flex-column">
                  <h3 className="text-uppercase">{category.display_name}</h3>
                </div>
                <div className="card-toolbar">
                  <Dropdown>
                    <Dropdown.Toggle variant="btn btn-sm btn-primary btn-icon">
                      <span className="svg-icon svg-icon-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <rect x="10" y="10" width="4" height="4" rx="2" fill="black"></rect>
                          <rect x="17" y="10" width="4" height="4" rx="2" fill="black"></rect>
                          <rect x="3" y="10" width="4" height="4" rx="2" fill="black"></rect>
                        </svg>
                      </span>
                    </Dropdown.Toggle>
                    <Dropdown.Menu variant="menu menu-sub menu-sub-dropdown menu-column menu-rounded menu-gray-800 menu-state-bg-light-primary w-250px p-3">
                      <Dropdown.Item bsPrefix="menu-item" onClick={executeShowModalCategoryUpdate}>
                        <p className="menu-link fw-bold px-5 m-0">{lang.labels.editCategory}</p>
                      </Dropdown.Item>
                      <Dropdown.Item bsPrefix="menu-item" onClick={submitCategoryDelete}>
                        <p className="menu-link fw-bold px-5 m-0">{lang.labels.deleteCategory}</p>
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </div>
              </div>
              <div className="card-body">
                <div className="mb-5">
                  <div className="fs-6 fw-bolder">{lang.labels.description}</div>
                  <div className="separator separator-dashed mt-1 mb-2"></div>
                  <div className="fw-bold text-justify">{category.description}</div>
                </div>
                <div className="mb-5">
                  <div className="fs-6 fw-bolder">{lang.labels.names}</div>
                  <div className="separator separator-dashed mt-1 mb-3"></div>
                  { Object.values(category.names).every((value) => (value.length > 0))
                    ?
                    <>
                      { Object.entries(category.names).map (( (item, index) => { return (
                        <div key={index} className={`${index < 3 && "pb-2"} ${item[1].length === 0 && "d-none"}`}>
                          <div className="fw-bold">{item[1]}</div>
                          <div className="fs-8 text-muted">{languageValue(lang, item[0].toUpperCase())}</div>
                        </div>
                      )}))}
                    </>
                    :
                    <div className="text-secondary text-justify">
                      {lang.labels.noRecordsFound}
                    </div>
                  }
                </div>
                <div className="mb-8">
                  <div className="fs-6 fw-bolder">{lang.labels.accountingAccounts}</div>
                  <div className="separator separator-dashed mt-1 mb-3"></div>
                  <div className="pb-2">
                    <div className="fs-8 text-muted d-flex align-items-center">
                      {lang.labels.expenseAccount}:
                      <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.purchases}><FaExclamationCircle /></i>
                    </div>
                    <div className="fw-bold">{accountingAccountSelected(accountingExpenseAccountsForCompany, category.accounts.expense, companyForUser.language)}</div>
                  </div>
                  <div className="pb-2">
                    <div className="fs-8 text-muted d-flex align-items-center">
                      {lang.labels.vatAccount}:
                      <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.purchases}><FaExclamationCircle /></i>
                    </div>
                    <div className="fw-bold">{accountingAccountSelected(accountingVatAccountsForCompany, category.accounts.vat, companyForUser.language)}</div>
                  </div>
                  <div className="pb-0">
                    <div className="fs-8 text-muted d-flex align-items-center">
                      {lang.labels.accountProduct}:
                      <i className="ms-2 fs-9" data-bs-toggle="tooltip" data-bs-trigger="hover" data-bs-original-title={lang.labels.sales}><FaExclamationCircle /></i>
                    </div>
                    <div className="fw-bold">{accountingAccountSelected(accountingIncomeAccountsForCompany, category.accounts.income, companyForUser.language)}</div>
                  </div>
                </div>
                <div className="bg-light-primary border border-primary border-dashed rounded text-justify py-2 px-3">
                  <small>{lang.labels.aCategoryContainServicesProducts}</small>
                </div>
              </div>
            </div>
          </div>
          <div className="d-flex flex-column flex-lg-row-fluid ms-lg-5">
            <div className="card card-flush">
              <div className="card-header pt-3">
                <div className="card-title flex-column">
                  <h5>{lang.labels.listCategoryItems}</h5>
                  <div className="text-muted fs-7 mt-1">
                    {lang.labels.total}
                    { category.services.length === 0 && category.products.length === 0 &&
                      <span className="text-lowercase text-muted fw-bolder mx-1">0 {lang.labels.items}</span>
                    }
                    { category.services.length > 0 &&
                      <span className="text-lowercase text-muted fw-bolder mx-1">{category.services.length} {lang.labels.services}</span>
                    }
                    { category.services.length > 0 && category.products.length > 0 && lang.labels.and }
                    { category.products.length > 0 &&
                      <span className="text-lowercase text-muted fw-bolder mx-1">{category.products.length} {lang.labels.products}</span>
                    }
                    {lang.labels.inThisCategory}
                  </div>
                </div>
                <div className="card-toolbar">
                  <button className="btn btn-light-primary" type="button" onClick={executeShowModalCategoryUpdateListing}>
                    <div className="d-flex align-items-center">
                      <span className="svg-icon svg-icon-2 ms-0">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path opacity="0.3" d="M21.25 18.525L13.05 21.825C12.35 22.125 11.65 22.125 10.95 21.825L2.75 18.525C1.75 18.125 1.75 16.725 2.75 16.325L4.04999 15.825L10.25 18.325C10.85 18.525 11.45 18.625 12.05 18.625C12.65 18.625 13.25 18.525 13.85 18.325L20.05 15.825L21.35 16.325C22.35 16.725 22.35 18.125 21.25 18.525ZM13.05 16.425L21.25 13.125C22.25 12.725 22.25 11.325 21.25 10.925L13.05 7.62502C12.35 7.32502 11.65 7.32502 10.95 7.62502L2.75 10.925C1.75 11.325 1.75 12.725 2.75 13.125L10.95 16.425C11.65 16.725 12.45 16.725 13.05 16.425Z" fill="black"/>
                          <path d="M11.05 11.025L2.84998 7.725C1.84998 7.325 1.84998 5.925 2.84998 5.525L11.05 2.225C11.75 1.925 12.45 1.925 13.15 2.225L21.35 5.525C22.35 5.925 22.35 7.325 21.35 7.725L13.05 11.025C12.45 11.325 11.65 11.325 11.05 11.025Z" fill="black"/>
                        </svg>
                      </span>
                      {lang.labels.editListing}
                    </div>
                  </button>
                </div>
              </div>
              <div className="card-body d-flex flex-column">
                { category.services.length === 0 && category.products.length === 0
                  ?
                  <EmptyState illustration={imageSearch} illustrationAlt="" title={lang.labels.noRecordsFound} description={lang.labels.startAddingNewElementsToThisCategory} />
                  :
                  <>
                    { category.services.length > 0 &&
                      <>
                        { category.products.length > 0 &&
                          <>
                            <div className="fw-bolder">{lang.labels.listServices}</div>
                            <div className="separator separator-dashed mb-3"></div>
                          </>
                        }
                        <div id="container-category-listing" className="flex-column-fluid scroll-y">
                          { category.services.map (( (item, index) => { return (
                            <div key={index} className="d-flex align-items-center position-relative mb-2 me-1">
                              <div className="position-absolute top-0 start-0 rounded h-100 bg-secondary w-5px"></div>
                              <div className="d-flex justify-content-center ms-5">
                                { item.photos.length > 0
                                  ?
                                  <Carousel slide={false} controls={false} indicators={false} touch={false}>
                                    { item.photos.map (( (item, index) => { return (
                                      <Carousel.Item key={index}>
                                        <img src={imageService(item)} className="d-block w-50px h-50px" alt={lang.labels.photography + index} />
                                      </Carousel.Item>
                                    )}))}
                                  </Carousel>
                                  :
                                  <div className="symbol symbol-50px">
                                    <span className="symbol-label">
                                      <img src={imgServiceDefault} alt={lang.labels.photography} className="w-100 h-100" />
                                    </span>
                                  </div>
                                }
                              </div>
                              <div className="mx-5">
                                <div className="fw-bold">{item.display_name}</div>
                                <div className="text-muted fs-8">{item.description}</div>
                              </div>
                              <Link to={"/main/inventory/service/update/" + item.id} className="btn btn-sm btn-light ms-auto">{lang.labels.edit}</Link>
                            </div>
                          )}))}
                        </div>
                      </>
                    }
                    { category.products.length > 0 &&
                      <>
                        { category.services.length > 0 &&
                          <>
                            <div className="fw-bolder mt-5">{lang.labels.listProducts}</div>
                            <div className="separator separator-dashed mb-3"></div>
                          </>
                        }
                        <div id="container-category-listing" className="flex-column-fluid scroll-y">
                          { category.products.map (( (item, index) => { return (
                            <div key={index} className="d-flex align-items-center position-relative mb-2 me-1">
                              <div className="position-absolute top-0 start-0 rounded h-100 bg-secondary w-5px"></div>
                              <div className="d-flex justify-content-center ms-5">
                                { item.photos.length > 0
                                  ?
                                  <Carousel slide={false} controls={false} indicators={false} touch={false}>
                                    { item.photos.map (( (item, index) => { return (
                                      <Carousel.Item key={index}>
                                        <img src={imageProduct(item)} className="d-block w-50px h-50px" alt={lang.labels.photography + index} />
                                      </Carousel.Item>
                                    )}))}
                                  </Carousel>
                                  :
                                  <div className="symbol symbol-50px">
                                    <span className="symbol-label">
                                      <img src={imgProductDefault} alt={lang.labels.photography} className="w-100 h-100" />
                                    </span>
                                  </div>
                                }
                              </div>
                              <div className="mx-5">
                                <div className="fw-bold">{item.display_name}</div>
                                <div className="text-muted fs-8">
                                  {lang.labels.variants}:
                                  <span className="ms-1">{item.variants.length}</span>
                                </div>
                              </div>
                              <Link to={"/main/inventory/product/update/" + item.id} className="btn btn-sm btn-light ms-auto">{lang.labels.edit}</Link>
                            </div>
                          )}))}
                        </div>
                      </>
                    }
                  </>
                }
              </div>
            </div>
          </div>
        </div>
        :
        <AppPreloader></AppPreloader>
      }
      <AppModalCategoryUpdate companyForUser={companyForUser} categoriesForCompany={categoriesForCompany} accountingExpenseAccountsForCompany={accountingExpenseAccountsForCompany} accountingVatAccountsForCompany={accountingVatAccountsForCompany} accountingIncomeAccountsForCompany={accountingIncomeAccountsForCompany} category={category} setCategoriesForCompany={setCategoriesForCompany} setCategory={setCategory}></AppModalCategoryUpdate>
      <AppModalCategoryUpdateListing companyForUser={companyForUser} servicesForCompany={servicesForCompany} productsForCompany={productsForCompany} category={category} categoryServices={categoryServices} categoryProducts={categoryProducts} setCategory={setCategory} setCategoryServices={setCategoryServices} setCategoryProducts={setCategoryProducts} loadServicesForCompany={loadServicesForCompany} loadProductsForCompany={loadProductsForCompany}></AppModalCategoryUpdateListing>
    </div>
  )
};

export default CategoryViewPage;
